import { Button } from "@focal/ui-kit";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@focal/ui-kit";
import { PropsWithChildren } from "react";
import { cn } from "@/lib/utils";

interface DialogModalProps {
  title: string;
  description: string;
  /**
   * Label for the confirm button. Always use a verb describing the action
   * (e.g., "Delete rule", "Disconnect", "Revoke MFA") — never "Yes" — so the
   * user knows exactly what happens when they click.
   */
  confirmLabel: string;
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  onClick: () => void;
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  /**
   * Destructive variant: confirm button
   * uses the `destructiveOutline` Button variant. Use for delete / revoke / discard
   * confirmations.
   */
  variant?: "destructive" | "default";
}

export const DialogModal = ({
  children,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  onClick,
  open,
  onOpenChange,
  variant = "default",
}: PropsWithChildren<DialogModalProps>) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogTrigger asChild>{children}</DialogTrigger>
    <DialogContent
      className="max-w-[calc(100vw-32px)] sm:max-w-md rounded-xl"
      data-cy="alert-dialog"
    >
      <DialogHeader className="space-y-2">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription className="whitespace-pre-wrap">
          {description}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="gap-2 justify-between sm:justify-between">
        <DialogClose asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-cy="alert-dialog-cancel"
          >
            {cancelLabel}
          </Button>
        </DialogClose>
        <DialogClose asChild>
          <Button
            type="button"
            variant={
              variant === "destructive" ? "destructiveOutline" : "default"
            }
            size="sm"
            onClick={() => onClick()}
            data-cy="alert-dialog-confirm"
          >
            {confirmLabel}
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
